import { type Booking } from '$lib/model/booking';
import { apiStore } from '$lib/stores/api';
import type { Axios } from 'axios';
import { get, writable } from 'svelte/store';
import { bookingStore } from './booking';

export type ReviewStats = {
	all: ReviewPeriodStats;
	period: ReviewPeriodStats;
	previousPeriod: ReviewPeriodStats;
};

export type ReviewPeriodStats = {
	averageRating: number;
	reviewCount: number;
};

export const unreadReviewsCountStore = writable<number>(0);
export const reviewStatsStore = writable<ReviewStats>({
	all: {
		averageRating: 0,
		reviewCount: 0
	},
	period: {
		averageRating: 0,
		reviewCount: 0
	},
	previousPeriod: {
		averageRating: 0,
		reviewCount: 0
	}
});

export function markReviewAsRead(booking: Booking) {
	if (!booking.surveyResult || booking.surveyResult.isRead) {
		return;
	}

	booking.surveyResult.isRead = true;
	bookingStore.set(get(bookingStore).map((r) => (r.id === booking.id ? booking : r)));
	unreadReviewsCountStore.update((value) => (value ? value - 1 : 0));

	(get(apiStore) as Axios)
		.post(`/survey-result/read`, { id: booking.surveyResult.id })
		.catch(() => {
			if (!booking.surveyResult) {
				return;
			}

			booking.surveyResult.isRead = false;
			bookingStore.set(get(bookingStore).map((r) => (r.id === booking.id ? booking : r)));
			unreadReviewsCountStore.update((value) => (value ? value + 1 : 1));
		});
}

export function markReviewAsUnread(booking: Booking) {
	if (!booking.surveyResult || !booking.surveyResult.isRead) {
		return;
	}

	booking.surveyResult.isRead = false;
	bookingStore.set(get(bookingStore).map((r) => (r.id === booking.id ? booking : r)));
	unreadReviewsCountStore.update((value) => (value ? value + 1 : 1));

	(get(apiStore) as Axios)
		.post(`/survey-result/unread`, { id: booking.surveyResult.id })
		.catch(() => {
			if (!booking.surveyResult) {
				return;
			}

			booking.surveyResult.isRead = true;
			bookingStore.set(get(bookingStore).map((r) => (r.id === booking.id ? booking : r)));
			unreadReviewsCountStore.update((value) => (value ? value - 1 : 0));
		});
}

export function markAllReviewsAsRead(callback: () => void | undefined) {
	const surveyResultsSetAsRead: Booking[] = [];
	const currentUnread = get(unreadReviewsCountStore);

	bookingStore.set(
		get(bookingStore).map((r) => {
			if (!r.surveyResult || r.surveyResult.isRead) {
				return r;
			}

			surveyResultsSetAsRead.push(r);
			r.surveyResult.isRead = true;
			return r;
		})
	);

	unreadReviewsCountStore.update(() => 0);

	(get(apiStore) as Axios)
		.post('/survey-result/read')
		.then(() => {
			if (callback) {
				callback();
			}
		})
		.catch(() => {
			// Restore the previous state on error
			bookingStore.set(
				get(bookingStore).map((r) => {
					if (surveyResultsSetAsRead.includes(r) && r.surveyResult) {
						r.surveyResult.isRead = false;
					}
					return r;
				})
			);
			unreadReviewsCountStore.update(() => currentUnread);
		});
}
